import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchProducts, fetchClubInfo } from '../services/api';
import categoryVetementDefault from '../assets/images/catvetement.webp';
import categoryGadgetDefault from '../assets/images/catgadget.jpg';
import templateProduct from '../data/templateProduct';

const defaultCategoryImages = {
  vetement: categoryVetementDefault,
  accessoire: categoryGadgetDefault
};

const clothingCategories = ['maillot', 'survetement', 'blouson'];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryImages, setCategoryImages] = useState({ vetement: '', accessoire: '' });
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    fetchProducts()
      .then((data) => setProducts(data))
      .catch(() => setError('Impossible de charger les produits.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchClubInfo()
      .then((data) => {
        const images = data.categoryImages || {};
        setCategoryImages({
          vetement: typeof images.vetement === 'string' ? images.vetement : '',
          accessoire: typeof images.accessoire === 'string' ? images.accessoire : ''
        });
      })
      .catch(() => {
        setCategoryImages({ vetement: '', accessoire: '' });
      });
  }, []);

  const collections = useMemo(() => {
    const grouped = { vetement: [], accessoire: [] };
    products.forEach((product) => {
      const key = clothingCategories.includes(product.category) ? 'vetement' : 'accessoire';
      grouped[key].push(product);
    });
    return grouped;
  }, [products]);

  const handleCategoryClick = (category) => {
    setActiveCategory((current) => (current === category ? null : category));
    const target = document.getElementById('shop-collections');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const visibleCategories = activeCategory ? [activeCategory] : ['vetement', 'accessoire'];

  if (loading) return <p className="cart-card__message cart-card__message--info">Chargement de la boutique...</p>;
  if (error) return <p className="cart-card__message cart-card__message--error">{error}</p>;

  return (
    <section className="shop-section" id="boutique">
      <header>
        <h1>Boutique MPA’CHI</h1>
        <p>Maillots, survêtements, accessoires et blousons officiels des rouges et jaunes.</p>
      </header>

      <div className="shop-category-grid">
        {['vetement', 'accessoire'].map((category) => (
          <article
            key={category}
            className={`shop-category-card${activeCategory === category ? ' shop-category-card--active' : ''}`}
            onClick={() => handleCategoryClick(category)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCategoryClick(category);
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={activeCategory === category}
          >
            <img src={categoryImages[category] || defaultCategoryImages[category]} alt={`Catégorie ${category}`} />
            <div className="shop-category-card__body">
              <p>Catégorie</p>
              <h3>{category === 'vetement' ? 'Vêtements officiels' : 'Accessoires officiels'}</h3>
              <p>
                {category === 'vetement'
                  ? 'Maillots domicile/extérieur, survêtements, blousons MPA’CHI.'
                  : 'Goodies collectors, écharpes, casquettes, badges et souvenirs.'}
              </p>
            </div>
          </article>
        ))}
      </div>

      <div className="shop-grid" id="shop-collections">
        {visibleCategories.map((categoryKey) => (
          <div key={categoryKey} className="shop-collection">
            <h2>{categoryKey === 'vetement' ? 'Collection Vêtements' : 'Collection Accessoires'}</h2>
            <div className="shop-collection__cards">
              {collections[categoryKey].map((product) => (
                <article key={product._id} className="shop-card">
                  <div className="shop-card__media">
                    <span className="shop-card__badge">{product.collection || "MPA'CHI"}</span>
                    <Link
                      to={`/produits/${product._id}`}
                      className="shop-card__media-link"
                      aria-label={`Voir ${product.name}`}
                    >
                      <img
                        src={
                          product.visuals?.[0] ||
                          categoryImages[categoryKey] ||
                          defaultCategoryImages[categoryKey] ||
                          templateProduct.image
                        }
                        alt={product.name}
                      />
                    </Link>
                  </div>
                  <div className="shop-card__body">
                    <h3>{product.name}</h3>
                    <p className="shop-card__description">{product.description}</p>
                    <p className="shop-card__price">{Number(product.price).toFixed(2)} €</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}
      </div>

    </section>
  );
}
